/**
 * CetusEd Admin Console — Rate Limit Status
 *
 * COMPLIANCE: Rate limiting protects the platform from abuse and ensures
 *   fair resource allocation across districts. Per the server API contract,
 *   the default rate limit is 120 req/min per district.
 *
 *   Districts consistently hitting rate limits may need plan upgrades or
 *   configuration review.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useRateLimits } from '../../api/hooks';
import { DataTable, type Column } from '../../ui/DataTable';
import type { RateLimitStatus } from '../../api/hooks';

export const RateLimits: React.FC = () => {
  const { data: limits, isLoading, error } = useRateLimits();

  const columns: Column<RateLimitStatus>[] = [
    { key: 'districtName', header: 'District', sortable: true },
    {
      key: 'currentRate',
      header: 'Current Rate',
      sortable: true,
      render: (row) => `${row.currentRate} req/min`,
    },
    {
      key: 'limit',
      header: 'Limit',
      sortable: true,
      render: (row) => `${row.limit} req/min`,
    },
    { key: 'remaining', header: 'Remaining', sortable: true },
    {
      key: 'violations24h',
      header: 'Violations (24h)',
      sortable: true,
      render: (row) => (
        <span className={row.violations24h > 0 ? 'text-warning' : ''}>
          {row.violations24h}
        </span>
      ),
    },
    {
      key: 'resetAt',
      header: 'Resets At',
      render: (row) => new Date(row.resetAt).toLocaleString(),
    },
  ];

  if (error) {
    return (
      <div className="admin-error" role="alert">
        Unable to load rate limit data.
      </div>
    );
  }

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/monitoring">Monitoring</Link> / Rate Limits
      </nav>

      <div className="page-header">
        <h1>Rate Limit Status</h1>
      </div>

      <DataTable
        columns={columns}
        data={limits || []}
        keyExtractor={(r) => r.districtId}
        isLoading={isLoading}
        emptyMessage="No rate limit data available."
      />
    </div>
  );
};
